import React from 'react';
import { Zap, Truck, User, Clock } from 'lucide-react';
import useGameStore from '../../store/gameStore';
import { EMPTY_ARRAY } from '../../store/stableEmpties';

/**
 * CommercialOpsWidget — encart « Missions flash en cours » du Dashboard
 * (04/02/2026, retour user prod). Rend visibles les opportunités commerciales
 * acceptées via la Messagerie (`cli-new-business-01`). Avant ce widget,
 * accepter l'opportunité mobilisait silencieusement camion + chauffeur sans
 * aucune indication UI — d'où la perception « ça bloque mais rien ne se
 * passe ». Rendu nul si aucune opportunité active.
 */
export default function CommercialOpsWidget() {
  const opps = useGameStore(s => s.commercialOpportunities || EMPTY_ARRAY);
  const vehicles = useGameStore(s => s.vehicles || EMPTY_ARRAY);
  const drivers = useGameStore(s => s.drivers || EMPTY_ARRAY);
  const totalDays = useGameStore(s => s.totalDays || 0);

  if (opps.length === 0) return null;

  return (
    <div
      className="rounded-lg border border-purple-500/40 bg-purple-500/5 px-4 py-3"
      data-testid="dashboard-commercial-ops-widget"
    >
      <div className="flex items-center justify-between gap-3 mb-2">
        <p className="text-xs font-bold text-foreground flex items-center gap-2">
          <Zap className="w-4 h-4 text-purple-300" />
          Missions flash en cours ({opps.length})
        </p>
        <span className="text-[10px] uppercase tracking-wider text-purple-300/70 font-mono">
          Règlement à la fin
        </span>
      </div>
      <div className="space-y-1.5">
        {opps.map(o => {
          const daysLeft = Math.max(0, o.endsOnDay - totalDays);
          const vehicle = vehicles.find(v => v.id === o.vehicleId);
          const driver = drivers.find(d => d.id === o.driverId);
          return (
            <div
              key={o.id}
              className="flex items-center justify-between gap-3 text-[11px] bg-ink-800/50 rounded px-2 py-1.5 border border-purple-500/20"
              data-testid={`commercial-op-${o.id}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-bold text-slate-100 truncate">{o.clientName}</span>
                <span className="text-slate-500 inline-flex items-center gap-1">
                  <Truck className="w-3 h-3" />
                  {vehicle ? (vehicle.model || vehicle.name || vehicle.id) : '—'}
                </span>
                <span className="text-slate-500 inline-flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {driver ? driver.name : '—'}
                </span>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <span className="text-money font-mono font-bold">
                  +{(o.treasuryReward || 0).toLocaleString('fr-FR')} €
                </span>
                <span
                  className={`inline-flex items-center gap-1 font-mono ${
                    daysLeft <= 1 ? 'text-amber-300 font-bold' : 'text-purple-300'
                  }`}
                >
                  <Clock className="w-3 h-3" />
                  {daysLeft === 0 ? 'fin aujourd\u2019hui' : `J+${daysLeft}`}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
